import React from 'react';
import { Box, Card, Typography } from '@mui/material';

/**
 * The frame every Stats section uses: one title size, the description under it, actions on the
 * right.
 *
 * Before this (2026-10-17) the sections had grown three looks — raised cards with an h6 title,
 * outlined boxes with a small subtitle, and bare headings over a grid of cards — so related
 * panels read as unrelated and some titles looked like captions. One component makes them the
 * same by construction.
 *
 * `frameless` is for a section whose content is already a grid of cards (follower history, one
 * card per account): same title row, no outer border, so cards do not sit inside a card.
 */
export const SectionCard: React.FC<{
  id?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons or filters, shown at the right of the title and wrapping below it on a phone */
  actions?: React.ReactNode;
  frameless?: boolean;
  children?: React.ReactNode;
}> = ({ id, title, description, actions, frameless, children }) => {
  const header = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: description ? 0.5 : 1.5 }}>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {actions && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</Box>}
      </Box>
      {description && (
        <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
    </>
  );

  if (frameless) {
    return (
      <Box id={id} component="section" sx={{ mb: 3 }}>
        {header}
        {children}
      </Box>
    );
  }

  return (
    <Card variant="outlined" id={id} component="section" sx={{ p: { xs: 2, sm: 2.5 }, mb: 3 }}>
      {header}
      {children}
    </Card>
  );
};
